import { resolveMapUrl } from './buildMapUrl';
import { el } from './dom';
import type { MapData } from './types';

/**
 * The only place an iframe `src` is ever assigned. `resolveMapUrl` is the
 * sole source of that string, and it never returns anything that hasn't
 * passed isSafeExternalUrl or been built by us from structured coordinates.
 */
export function renderMap(container: HTMLElement, data: MapData | undefined): void {
  const wrapper = el({ tag: 'div', className: 'kia-map' });
  const url = resolveMapUrl(data);

  if (!url) {
    wrapper.append(
      el({
        tag: 'p',
        className: 'kia-map-fallback',
        text: 'Map is unavailable for this response.',
      }),
    );
    container.append(wrapper);
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.src = url;
  iframe.title = 'Map';
  iframe.loading = 'lazy';
  iframe.referrerPolicy = 'no-referrer-when-downgrade';
  // Google Maps' embed iframe needs scripts + same-origin to function, and
  // opens some links (e.g. "More options") in a new tab. Without
  // allow-popups-to-escape-sandbox, that new tab still inherits the
  // iframe's sandboxed (opaque) origin, and Chrome then refuses to
  // navigate it to maps.google.com — which enforces its own
  // Cross-Origin-Opener-Policy — failing with net::ERR_BLOCKED_BY_RESPONSE.
  // Letting the popup escape the sandbox lets it open as an ordinary,
  // unsandboxed tab instead.
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox',
  );
  // Customizable via CSS custom properties (fallbacks below apply until a
  // publisher sets them) — see "Styling" in README.md.
  iframe.style.border = 'var(--kia-map-border, 0)';
  iframe.style.width = 'var(--kia-map-width, 100%)';
  iframe.style.height = 'var(--kia-map-height, 240px)';
  iframe.className = 'kia-map-frame';

  wrapper.append(iframe);
  container.append(wrapper);
}
